import { Controller } from '@hotwired/stimulus';

/**
 * Shows a dropdown of similar existing registers below a name/lastName input.
 * Uses the existing /registers/search?q=… endpoint.
 *
 * Usage:
 *   data-controller="register-duplicate-hint"
 *   data-register-duplicate-hint-url-value="/registers/search"
 *
 * On the input:
 *   data-action="input->register-duplicate-hint#onInput"
 */
export default class extends Controller {

    static values = { url: String };
    declare readonly urlValue: string;

    private dropdown: HTMLElement | null = null;
    private debounceTimer: ReturnType<typeof setTimeout> | null = null;

    connect(): void {
        document.addEventListener('click', this.onDocumentClick);
    }

    disconnect(): void {
        document.removeEventListener('click', this.onDocumentClick);
        this.removeDropdown();
        if (this.debounceTimer) clearTimeout(this.debounceTimer);
    }

    onInput(event: Event): void {
        const input = event.currentTarget as HTMLInputElement;
        const q = input.value.trim();

        if (this.debounceTimer) clearTimeout(this.debounceTimer);

        if (q.length < 2) {
            this.removeDropdown();
            return;
        }

        this.debounceTimer = setTimeout(() => { void this.search(q, input); }, 400);
    }

    onBlur(): void {
        // Delay to allow clicks inside the dropdown to register first.
        setTimeout(() => this.removeDropdown(), 150);
    }

    private async search(q: string, anchor: HTMLInputElement): Promise<void> {
        try {
            const url = `${this.urlValue}?q=${encodeURIComponent(q)}`;
            const res = await fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
            if (!res.ok) return;
            const data = await res.json() as { id: number; fullName: string; legacyId: number | null }[];
            this.renderDropdown(data, anchor);
        } catch {
            // network errors — silently ignore
        }
    }

    private renderDropdown(items: { id: number; fullName: string; legacyId: number | null }[], anchor: HTMLInputElement): void {
        this.removeDropdown();
        if (!items.length) return;

        const dropdown = document.createElement('div');
        dropdown.className = 'list-group shadow position-absolute z-3 w-100';
        dropdown.style.maxHeight = '200px';
        dropdown.style.overflowY = 'auto';

        for (const item of items) {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'list-group-item list-group-item-action d-flex justify-content-between align-items-center small';
            btn.innerHTML = `<span><i class="bi bi-person me-1 text-warning"></i>${this.esc(item.fullName)}</span>
                             <span class="badge bg-secondary">#${item.legacyId ?? item.id}</span>`;
            dropdown.appendChild(btn);
        }

        const wrapper = anchor.closest('.mb-3, .col-md-3, .col-md-4, .col') ?? anchor.parentElement;
        if (wrapper) {
            (wrapper as HTMLElement).style.position = 'relative';
            wrapper.appendChild(dropdown);
        }

        this.dropdown = dropdown;
    }

    private removeDropdown(): void {
        this.dropdown?.remove();
        this.dropdown = null;
    }

    private onDocumentClick = (e: Event): void => {
        if (!this.dropdown?.contains(e.target as Node)) {
            this.removeDropdown();
        }
    };

    private esc(str: string): string {
        return str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] ?? c));
    }
}
